import * as THREE from 'three';
import { POI } from './POI';
import { Color } from '../types/Color';

/**
 * POIConnectionSystem -- creates and updates tube meshes between connected POIs.
 * Each POI can specify `connectTo: "0, 2"` to draw tubes to POI index 0 and 2.
 * Tubes are deduplicated by sorted index pair so each connection is rendered once.
 */
export class POIConnectionSystem {
    private _group: THREE.Group;
    private _tubes: Map<string, THREE.Mesh> = new Map();

    constructor(parentScene: THREE.Scene) {
        this._group = new THREE.Group();
        this._group.name = 'POIConnections';
        parentScene.add(this._group);
    }

    /**
     * Rebuild all connection tubes based on current POI positions and connectTo values.
     * Call after layout application or when positions change.
     */
    update(pois: POI[]): void {
        // Clear old tubes
        for (const mesh of this._tubes.values()) {
            this._group.remove(mesh);
            mesh.geometry.dispose();
            (mesh.material as THREE.Material).dispose();
        }
        this._tubes.clear();

        // Build new tubes
        for (let i = 0; i < pois.length; i++) {
            const poi = pois[i];
            const connectStr = poi.poidata.connectTo?.value;
            if (!connectStr) continue;

            const targetIndices = connectStr.split(',')
                .map(s => parseInt(s.trim()))
                .filter(n => !isNaN(n) && n >= 0 && n < pois.length && n !== i);

            const radius = poi.poidata.tubeRadius?.value ?? 0.03;
            const color = poi.poidata.tubeColor?.value ?? new Color('#ffffff');

            for (const targetIdx of targetIndices) {
                const key = `${Math.min(i, targetIdx)}-${Math.max(i, targetIdx)}`;
                if (this._tubes.has(key)) continue; // avoid duplicate tubes

                const startPos = poi.transform.getWorldPosition();
                const endPos = pois[targetIdx].transform.getWorldPosition();

                const tube = this._createTube(startPos, endPos, radius, color);
                tube.name = `tube_${key}`;
                this._group.add(tube);
                this._tubes.set(key, tube);
            }
        }
    }

    /**
     * Create a cylinder mesh oriented between two world-space points.
     */
    private _createTube(start: { x: number; y: number; z: number }, end: { x: number; y: number; z: number }, radius: number, color: Color): THREE.Mesh {
        const startVec = new THREE.Vector3(start.x, start.y, start.z);
        const endVec = new THREE.Vector3(end.x, end.y, end.z);
        const direction = new THREE.Vector3().subVectors(endVec, startVec);
        const length = direction.length();
        const midpoint = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);

        const geometry = new THREE.CylinderGeometry(radius, radius, length, 8, 1);
        const material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(color.r, color.g, color.b),
            roughness: 0.4,
            metalness: 0.2
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(midpoint);

        // Orient cylinder from start to end
        const axis = new THREE.Vector3(0, 1, 0);
        const quaternion = new THREE.Quaternion().setFromUnitVectors(axis, direction.normalize());
        mesh.setRotationFromQuaternion(quaternion);

        return mesh;
    }

    /**
     * Clean up all tubes and remove the group from the scene.
     */
    dispose(): void {
        for (const mesh of this._tubes.values()) {
            this._group.remove(mesh);
            mesh.geometry.dispose();
            (mesh.material as THREE.Material).dispose();
        }
        this._tubes.clear();
        this._group.parent?.remove(this._group);
    }
}
